import { ErrorPanel, FetchData } from "@/components/atoms";
import { ExportDialog, ExportOptions } from "@/components/molecules";
import useExportAudience from "@/components/organisms/Audience/Export/use-export-audience";
import {tagCategories, exportChannels} from "@/constants";
import { useTagsList } from "@/hooks";
import { AudienceSize, Tag } from "@/types";
import { useAuth0 } from "@auth0/auth0-react";
import { Grid, TextField, Typography } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import React, { useEffect, useState } from "react";

import { useExportStyles } from "./Export.styles";

interface ExportProps {
  audienceSize: AudienceSize | null;
  globalTags: Tag[];
  globalSelection: Tag[];
  onFileDownload: () => void;
  onSelectionUpdate: (tags: Tag[]) => void;
  onTagsUpdate: (tags: Tag[]) => void;
}

const Export: React.FC<ExportProps> = ({
  audienceSize,
  globalTags,
  globalSelection,
  onFileDownload,
  onSelectionUpdate,
  onTagsUpdate,
}) => {
  const styles = useExportStyles();
  const { getAccessTokenSilently } = useAuth0();

  const {
    getTagsData,
    loading: tagsListLoading,
    error: tagsListError,
  } = useTagsList();

  const {
    exportAudienceData,
    loading: exportLoading,
    error: exportError,
  } = useExportAudience();

  const [selection, setSelection] = useState<Array<Tag>>(globalSelection);
  const [tags, setTags] = useState<Array<Tag>>(globalTags);
  const [category, setCategory] = useState("Interests");
  const [open, setOpen] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>(tagCategories);

  const allowExport = permissions.includes("postcode:export");

  const [selectedExportOption, setSelectedExportOption] = useState("");
  const [selectedExportChannel, setSelectedExportChannel] = useState("");


  useEffect(() => {
    if (globalTags.length > 0) {
      setTags(globalTags);
    } else {
      const fetchData = async () => {
        const data = await getTagsData();
        if (data) {
          onTagsUpdate(data);
        }
      };
      fetchData();
    }
    setSelection(globalSelection);
  }, [globalTags, globalSelection]);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const token = await getAccessTokenSilently();
        const decodedToken: any = JSON.parse(atob(token.split(".")[1]));
        setPermissions(
          decodedToken[
            `https://${process.env.REACT_APP_AUTH0_DOMAIN}/permissions`
          ] || []
        );
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    };

    fetchPermissions();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (permissions.includes('postcode:snoop')) setCategories((prevState) => [...prevState, 'Open Banking']);
    if (permissions.includes('postcode:mastercard')) setCategories((prevState) => [...prevState, 'Spend Categories'])
  }, [permissions]);

  const handleStartExport = (selectedOption: string) => {
    setSelectedExportOption(selectedOption);
    setOpen(true);
  };

  const handleCategorySelect = (e: React.ChangeEvent<HTMLInputElement>) =>
    setCategory(e.target.value);

  const handleSelectionUpdate = (_e: React.ChangeEvent<{}>, value: Tag[]) =>
    onSelectionUpdate(value);

  const handleDelete = (value: number) => () => {
    const newSelection = selection.filter((v) => v.tag_id !== value);
    onSelectionUpdate(newSelection);
  };

  const handleCheckClose = () => setOpen(false);

  const handleExport = async () => {
    if (selectedExportOption && audienceSize) {
      const data = await exportAudienceData(
        selection.map((v) => v.tag_id),
        selectedExportOption,
        audienceSize
      );

      if (data) {
        window.open(data.url, "_blank");
        onFileDownload();
        handleCheckClose();
        setOpen(false);
      }
    }
  };

  if (tagsListLoading) return <FetchData message="Loading tags" />;

  if (exportLoading) return <FetchData message="Preparing data for download" />;

  if (exportError || tagsListError) {
    return (
      <ErrorPanel
        error={exportError || tagsListError || ""}
        errorMessage={exportError ?? tagsListError ?? ""}
      />
    );
  }
  return (
    <Grid container spacing={3} alignItems="center" alignContent="center">
      <Grid item xs={12}>
        <Typography gutterBottom variant="h6" className={styles.cardHeader}>
          Export
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <TextField
          fullWidth
          id="export-option-select"
          label="Select a Channel"
          variant="filled"
          select
          value={selectedExportChannel}
          onChange={(e) => setSelectedExportChannel(e.target.value)}
          InputProps={{ disableUnderline: true }}
        >
          {exportChannels.map((option, index) => (
            <MenuItem key={index} value={option.name}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <ExportOptions
        allowExport={allowExport}
        category={category}
        categories={categories}
        selectedExportChannel={selectedExportChannel}
        selection={selection}
        tags={tags}
        onCategorySelect={handleCategorySelect}
        onSelectionUpdate={handleSelectionUpdate}
        onDelete={handleDelete}
        onStartExport={handleStartExport}
      />
      <ExportDialog
        open={open}
        onConfirm={handleExport}
        onClose={handleCheckClose}
      />
    </Grid>
  );
};

export default Export;
